import React, { useRef } from 'react';
import { type Maybe } from '@gql/schema';
import loadable from '@loadable/component';
import {
  AdvertisementContainer,
  Countdown,
  IPageName,
  defaultMapStyles,
  saleEndsWithinCountdownPeriod,
} from '@secretescapes/cauldron';
import {
  ContentBoxExpandible,
  Device,
  FormattedText,
  type IMapOptionsProps,
  type IMarkerProps,
  InfoBox,
  InfoBoxSize,
  InfoBoxVariant,
  StaticMap,
  TypographyVariant,
  defaultHtmlMapping,
  useDeviceViewport,
} from '@secretescapes/falcon';
import { i18n } from '@secretescapes/i18n';
import * as Sentry from '@sentry/react';
import { useAppState } from '@utils/useAppState';
import { useApplicationState } from '@utils/useApplicationState';
import { BullfinchFeatureFlags, useFeatureFlag } from '@utils/useFeatureFlag';
import { useTranslations } from '@utils/useTranslations';
import LazyLoad from 'react-lazyload';
import { AvailabilityPresentation } from '@features/AvailabilityPresentation';
import { type AvailabilityPresentationHandle } from '@features/AvailabilityPresentation/AvailabilityPresentation.interfaces';
import { notEmpty } from '@features/AvailabilityPresentation/AvailabilityPresentation.utils';
import { Gallery } from '@features/Gallery';
import { Score } from '@features/Reviews';
import { PropertyHighlightsBadges } from '@features/Reviews/PropertyHighlightsBadges';
import { DetailsSection } from '@features/Sale/Details/UncontrolledDetails';
import { AssistedSaleBanner } from './AssistedSaleBanner';
import { Badges } from './Badges';
import { Breadcrumbs } from './Breadcrumbs';
import { Facilities, FacilitiesWarning } from './Facilities';
import { InteractionButtons } from './InteractionButtons';
import { MaintenanceBox } from './MaintenanceBox';
import {
  type CustomRef,
  Navigation,
  SectionId,
  StickyNavigation,
  getRefHeight,
} from './Navigation';
import { OmnibusDirective } from './OmnibusDirective';
import { PayPalMessages } from './PayPal/PayPalMessages';
import { RecommendedSales } from './RecommendedSales';
import { RetryBox } from './RetryBox';
import { type SalePageProps } from './SalePage.interfaces';
import {
  AdContainer,
  AdWrappedContentContainer,
  AdditionalInformationContainer,
  BadgesWrapper,
  ContentContainer,
  DetailsContainer,
  FullWidthAdContainer,
  GallerySection,
  HeaderSection,
  HighlightsContainer,
  OmnibusDirectiveWrapper,
  RecommendedSalesSection,
  SalePageDesktopWrapper,
  SaleSummaryWrapper,
  StaticMapContainer,
  StickyAdWrapper,
  SupplementaryInformationContainer,
  TopBoxContainer,
} from './SalePageDesktop.styles';
import { DEFAULT_ICON_PNG_URL } from './SalePageMobile';
import { Section, useForceSectionInNav } from './Section';
import { TitleSection } from './TitleSection';
import { useFlashsalesHeights } from './useFlashsalesHeights';
import { useScroll } from './useScroll';

const AvailabilityPresentationWrapper = loadable(
  () =>
    import(
      /* webpackChunkName: "AvailabilityPresentationWrapper" */ '../../features/AvailabilityPresentation/SelectionContext'
    ),
  {
    resolveComponent: components => components.SelectionProvider,
    ssr: false,
  }
);

const MAP_FIXED_HEIGHT = 248;

export const SalePageDesktop = ({
  saleId,
  isFavourite,
  saleTitle,
  destinationName,
  images,
  summary,
  maintenance,
  mainParagraph,
  hotelDetails,
  highlightsText,
  staticMapSrc,
  googleMapsKey,
  woosmapApiKey,
  mapProvider = 'google',
  mapAddress,
  location,
  facilities,
  locale,
  endDate,
  pricingRules,
  discount,
  discountTooltip,
  isTimeLimited,
  badges,
  warningMessageBody,
  showAdvertisements,
  showPricingAndAvailability,
  flightPriceAvailable,
  theme,
  showReviews,
  reviews,
  refetchFavourites,
  showAssistedSaleBanner,
  isFamilySale,
  isExpiredSale,
  previewMode,
}: SalePageProps) => {
  const {
    score,
    scoreDescription,
    totalReviewsCount,
    backgroundColor,
    textColor,
    badges: propertyHighlights,
  } = reviews ?? {};
  const { showUrgentDiscount } = pricingRules;
  const { latitude, longitude } = location ?? {};

  const { finalRates } = useAppState();
  const { translations } = useTranslations();
  const { affiliateCode } = useApplicationState();

  const deviceViewport = useDeviceViewport();
  const isDesktop = deviceViewport === Device.desktop;

  const availabilityPresentationRef = useRef<AvailabilityPresentationHandle | null>(null);

  const { headerHeight } = useFlashsalesHeights();

  const stickyRef = useRef<HTMLDivElement | null>(null);

  const { scrollToRef, scrollToSection } = useScroll(stickyRef);

  const displayImages = images.length > 0;
  const hasFacilities = facilities.flattenedFacilities.length > 0;
  const displayFacilities = previewMode || hasFacilities;

  const initialSectionRefs: CustomRef[] = [
    { id: SectionId.Photos, display: displayImages },
    { id: SectionId.Property, display: true },
    {
      id: SectionId.Facilities,
      display: displayFacilities,
    },
    { id: SectionId.Location, display: true },
    { id: SectionId.Offers, display: !!showPricingAndAvailability },
  ];

  const sectionRefs = useRef<CustomRef[]>(initialSectionRefs);

  useForceSectionInNav(sectionRefs, SectionId.Offers, !!showPricingAndAvailability);

  const { isOutageOngoing, outageEndDate } = maintenance ?? {};

  const addSectionRef = (id: SectionId, element: Maybe<HTMLElement>) => {
    sectionRefs.current = sectionRefs.current.map(ref =>
      ref.id === id && !ref.element ? { ...ref, element } : ref
    );
  };

  const menuHeight = headerHeight + getRefHeight(stickyRef);

  const markers: IMarkerProps[] = [
    {
      id: '1',
      markerIconUrl: DEFAULT_ICON_PNG_URL,
      position: { lat: latitude ?? 0, lng: longitude ?? 0 },
    },
  ];

  const mapOptions: IMapOptionsProps = {
    styles: defaultMapStyles,
    zoomControl: true,
  };

  const isExtendedPeriod = useFeatureFlag(BullfinchFeatureFlags.extendedPeriodCountdown);

  const displayCountdown =
    !isExpiredSale && isTimeLimited && saleEndsWithinCountdownPeriod(endDate, isExtendedPeriod);

  const scrollToSectionById = (sectionId: SectionId) => {
    const section = sectionRefs.current.find(ref => ref.id === sectionId);

    if (section) {
      scrollToSection(section);
    }
  };

  const scrollToCalendarView = () => {
    if (availabilityPresentationRef.current) {
      scrollToRef(availabilityPresentationRef.current.container, 'top');
    }
  };

  const handleNavigationCtaClick = () => {
    if (finalRates.show) {
      scrollToSectionById(SectionId.Offers);
    } else {
      scrollToCalendarView();
      availabilityPresentationRef.current?.openCalendar();
      if (!performance.getEntriesByName('start-render')?.length) {
        performance.mark('start-render');
      }
    }
  };

  return (
    <>
      {warningMessageBody || isExpiredSale ? (
        <AdditionalInformationContainer>
          {warningMessageBody ? (
            <TopBoxContainer data-testid="warning-message">
              <InfoBox
                subTitle={warningMessageBody}
                variant={InfoBoxVariant.alert}
                size={InfoBoxSize.large}
              />
            </TopBoxContainer>
          ) : (
            <LazyLoad once={true}>
              <RecommendedSales isExpiredSale={true} saleIds={[saleId]} />
            </LazyLoad>
          )}
        </AdditionalInformationContainer>
      ) : null}
      <SalePageDesktopWrapper>
        <HeaderSection id={SectionId.Title}>
          {location && <Breadcrumbs location={location} />}
          <TitleSection
            title={saleTitle}
            subtitle={destinationName}
            onClick={() => scrollToSectionById(SectionId.Location)}
          />
          <SupplementaryInformationContainer>
            <BadgesWrapper>
              <Badges badges={badges} />
              {displayCountdown && <Countdown endDate={endDate} salePageVariant={true} />}
              {showReviews && score ? (
                <Score
                  score={score}
                  scoreDescription={scoreDescription ?? ''}
                  backgroundColor={backgroundColor ?? ''}
                  textColor={textColor ?? ''}
                  reviewsCount={totalReviewsCount ?? 0}
                />
              ) : null}
            </BadgesWrapper>
            <InteractionButtons
              saleId={saleId}
              saleTitle={saleTitle}
              isFavourite={!!isFavourite}
              isMobile={false}
            />
          </SupplementaryInformationContainer>
        </HeaderSection>
        {displayImages && (
          <GallerySection
            id={SectionId.Photos}
            applyRef={addSectionRef}
            data-testid="gallery-desktop"
          >
            <Gallery
              images={images}
              saleId={saleId}
              saleTitle={saleTitle}
              isFavourite={!!isFavourite}
              isMobile={false}
              showUrgentDiscount={!!showUrgentDiscount}
              discount={discount}
              discountTooltip={discountTooltip}
              refetchFavourites={refetchFavourites}
            />
          </GallerySection>
        )}
        <StickyNavigation
          stickyRef={stickyRef}
          navigation={
            <Navigation
              stickyRef={stickyRef}
              sectionRefs={sectionRefs}
              onCtaClick={handleNavigationCtaClick}
            />
          }
        >
          <ContentContainer>
            <AdWrappedContentContainer>
              {showAdvertisements && isDesktop ? (
                <AdContainer side="left">
                  <StickyAdWrapper menuHeight={`${menuHeight}px`}>
                    <AdvertisementContainer
                      affiliate={affiliateCode}
                      page={IPageName.offers}
                      adKey="offers_skyScraperLeft"
                    />
                  </StickyAdWrapper>
                </AdContainer>
              ) : null}
              <Section
                id={SectionId.Property}
                applyRef={addSectionRef}
                title={i18n('sale.hotelDetails.label', [], translations)}
              >
                <div>
                  <SaleSummaryWrapper>
                    {showReviews && propertyHighlights?.length ? (
                      <PropertyHighlightsBadges
                        propertyHighlights={propertyHighlights.filter(notEmpty)}
                      />
                    ) : null}
                    <FormattedText html={summary} fontColor={theme?.color?.blackText} />
                  </SaleSummaryWrapper>
                  <ContentBoxExpandible
                    showLessLabel={i18n('sales.filter.showless', [], translations)}
                    showMoreLabel={i18n('sales.filter.showmore', [], translations)}
                    maxLinesInCollapsedState={10}
                  >
                    <DetailsContainer>
                      <DetailsSection description={mainParagraph} id="mainParagraph" />
                      <DetailsSection description={hotelDetails} id="hotelDetails" />
                    </DetailsContainer>
                  </ContentBoxExpandible>
                </div>
              </Section>
              {displayFacilities && (
                <Section
                  id={SectionId.Facilities}
                  applyRef={addSectionRef}
                  title={i18n('facilities_title', [], translations)}
                >
                  {hasFacilities ? <Facilities facilities={facilities} /> : <FacilitiesWarning />}
                </Section>
              )}
              <Section
                id={SectionId.Location}
                applyRef={addSectionRef}
                title={i18n('sale.locationAndNearby.title', [], translations)}
              >
                <>
                  <Sentry.ErrorBoundary
                    beforeCapture={scope => scope.setTag('error.boundary', 'map')}
                    fallback={<RetryBox retryFunction={() => window?.location?.reload()} />}
                  >
                    {staticMapSrc && (
                      <StaticMapContainer data-testid="static-map">
                        <StaticMap
                          id={mapProvider === 'woosmap' ? 'woosmap-static-map' : 'falcon-map'}
                          staticMapSrc={staticMapSrc}
                          woosmapApiKey={woosmapApiKey}
                          provider={mapProvider}
                          googleMapsKey={googleMapsKey}
                          googleMapsLanguage={locale}
                          googleMapsRegion={locale}
                          markers={markers}
                          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                          options={mapOptions}
                          address={mapAddress}
                          fullscreenOnClick={false}
                          setStaticMaxWidth={false}
                          height={MAP_FIXED_HEIGHT}
                        />
                      </StaticMapContainer>
                    )}
                  </Sentry.ErrorBoundary>
                  <HighlightsContainer>
                    <FormattedText
                      html={highlightsText}
                      fontColor={theme?.color?.blackText}
                      htmlMapping={{
                        ...defaultHtmlMapping,
                        a: TypographyVariant.linkSecondary,
                      }}
                    />
                  </HighlightsContainer>
                  {showAssistedSaleBanner && (
                    <AssistedSaleBanner isFamilyVariant={isFamilySale} isMobile={false} />
                  )}
                </>
              </Section>
              {showAdvertisements && isDesktop ? (
                <AdContainer side="right">
                  <StickyAdWrapper menuHeight={`${menuHeight}px`}>
                    <AdvertisementContainer
                      affiliate={affiliateCode}
                      page={IPageName.offers}
                      adKey="offers_skyScraperRight"
                    />
                  </StickyAdWrapper>
                </AdContainer>
              ) : null}
            </AdWrappedContentContainer>
            {/* Offers and calendar load client-side on page load  */}
            {showPricingAndAvailability ? (
              <Section
                className={'full-width'}
                id={SectionId.Offers}
                applyRef={addSectionRef}
                title={i18n('sale.offersAndAvailability.title', [], translations)}
              >
                <>
                  {isOutageOngoing ? (
                    <MaintenanceBox endDate={outageEndDate ?? ''} isMobile={false} />
                  ) : (
                    <AvailabilityPresentationWrapper>
                      <PayPalMessages textSize={16} />
                      <AvailabilityPresentation
                        flightPriceAvailable={flightPriceAvailable}
                        scrollToSection={() => scrollToCalendarView()}
                        ref={availabilityPresentationRef}
                      />
                    </AvailabilityPresentationWrapper>
                  )}
                </>
              </Section>
            ) : null}
            <OmnibusDirectiveWrapper>
              <OmnibusDirective isMobile={false} />
            </OmnibusDirectiveWrapper>
            {/* Recommended sales load client-side when user scrolls down  */}
            <LazyLoad once={true} offset={100}>
              {!isExpiredSale ? (
                <RecommendedSalesSection title={i18n('sale.recommendations', [], translations)}>
                  <RecommendedSales saleIds={[saleId]} isExpiredSale={false} />
                </RecommendedSalesSection>
              ) : null}
            </LazyLoad>
            {showAdvertisements ? (
              <FullWidthAdContainer>
                <AdvertisementContainer
                  affiliate={affiliateCode}
                  page={IPageName.offers}
                  adKey="offers_fullWidthReplacement"
                />
              </FullWidthAdContainer>
            ) : null}
          </ContentContainer>
        </StickyNavigation>
      </SalePageDesktopWrapper>
    </>
  );
};
